import { useState } from 'react'

import {
  WorkingTimePercentagesDocument,
  WorkingTimePercentagesQuery,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

const CENTS = 100.0
const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24

type ContractVersionNode =
  WorkingTimePercentagesQuery['contracts']['contractVersionsConnection']['nodes'][number]

export const useWorkingTimePercentages = ({
  periodEnd,
  periodStart,
  employeeIds,
  isEmployeeCostsPage,
}: {
  periodEnd: string
  periodStart: string
  employeeIds?: string[]
  isEmployeeCostsPage: boolean
}) => {
  const [finishedLoading, setFinishedLoading] = useState(false)
  const [allWorkingTimePercentages, setAllWorkingTimePercentages] = useState<{
    [key: string]: number | null | undefined
  }>({})
  const [nonReferenceContracts, setNonReferenceContracts] = useState<{
    [key: string]: ContractVersionNode
  }>({})

  const [fetchWorkingTimePercentages, { data }] = useLazyQuery(WorkingTimePercentagesDocument, {
    fetchPolicy: 'no-cache',
  })

  const totalDays = useUnsafeMemo(() => {
    return (
      (new Date(periodEnd).getTime() - new Date(periodStart).getTime()) / MILLISECONDS_IN_DAY + 1
    )
  }, [periodEnd, periodStart])

  const isContractWithinPeriod = useUnsafeCallback(
    (contractStart: string, contractEnd?: string | null) => {
      return (
        new Date(contractStart) <= new Date(periodEnd) &&
        (!contractEnd || new Date(contractEnd) >= new Date(periodStart))
      )
    },
    [periodEnd, periodStart]
  )

  const hasMissingWorkingTimePercentage = useUnsafeMemo(() => {
    return Object.values(allWorkingTimePercentages).some((workingTimePercentageInCents) => {
      return workingTimePercentageInCents === null || workingTimePercentageInCents === undefined
    })
  }, [allWorkingTimePercentages])

  const prorate = useUnsafeCallback(
    (percentage: number, contractStart: string, contractEnd?: string | null) => {
      const start =
        new Date(contractStart) > new Date(periodStart)
          ? new Date(contractStart)
          : new Date(periodStart)
      const end =
        contractEnd && new Date(contractEnd) < new Date(periodEnd)
          ? new Date(contractEnd)
          : new Date(periodEnd)

      if (start > end) return 0

      const activeDays = (end.getTime() - start.getTime()) / MILLISECONDS_IN_DAY + 1

      return percentage * (activeDays / totalDays)
    },
    [totalDays, periodStart, periodEnd]
  )

  const averageHeadcount = useUnsafeMemo(() => {
    const validPercentages = Object.values(allWorkingTimePercentages).filter(
      (percentage) => percentage !== null && percentage !== undefined
    )

    const averageInCents = validPercentages.length
      ? validPercentages.reduce((a, b) => a + b, 0) / validPercentages.length
      : 0

    const average = averageInCents / CENTS
    const headCount = employeeIds?.length || 0

    return ((average / 100.0) * headCount).toFixed(2)
  }, [employeeIds, allWorkingTimePercentages])

  useUnsafeEffect(() => {
    if (isEmployeeCostsPage) {
      setNonReferenceContracts({})
      setAllWorkingTimePercentages({})
      setFinishedLoading(employeeIds?.length === 0)

      if (!employeeIds?.length) return

      fetchWorkingTimePercentages({
        variables: { employeeIds, after: null, endDate: periodEnd },
      })
    }
  }, [
    employeeIds,
    periodEnd,
    periodStart,
    isEmployeeCostsPage,
    fetchWorkingTimePercentages,
  ])

  useUnsafeEffect(() => {
    if (finishedLoading) return
    if (!data?.contracts?.contractVersionsConnection) return

    if (hasMissingWorkingTimePercentage) {
      setFinishedLoading(true)
      return
    }

    const newPercentages: { [key: string]: number | null | undefined } = {}
    const batchNonReferenceContracts: { [key: string]: ContractVersionNode } = {}

    const connection = data.contracts.contractVersionsConnection

    connection.nodes?.forEach((node) => {
      if (node.id && node.startsOn && isContractWithinPeriod(node.startsOn, node.endsOn)) {
        if (node.isReference) {
          if (
            node.workingTimePercentageInCents !== null &&
            node.workingTimePercentageInCents !== undefined
          ) {
            newPercentages[node.id] = prorate(
              node.workingTimePercentageInCents,
              node.startsOn,
              node.endsOn
            )
          } else {
            newPercentages[node.id] = node.workingTimePercentageInCents
          }
        } else {
          batchNonReferenceContracts[node.id] = node
        }
      }
    })

    setNonReferenceContracts((prev) => ({ ...prev, ...batchNonReferenceContracts }))
    setAllWorkingTimePercentages((prevNodes) => ({ ...prevNodes, ...newPercentages }))

    if (connection.pageInfo.hasNextPage) {
      const nextCursor = connection.pageInfo.endCursor

      fetchWorkingTimePercentages({
        variables: {
          after: nextCursor,
          endDate: periodEnd,
          employeeIds: employeeIds ?? [],
        },
      })
    } else {
      setFinishedLoading(true)
    }
  }, [
    data,
    prorate,
    periodEnd,
    periodStart,
    employeeIds,
    finishedLoading,
    isContractWithinPeriod,
    fetchWorkingTimePercentages,
    hasMissingWorkingTimePercentage,
  ])

  return {
    averageHeadcount,
    loading: !finishedLoading,
    hasMissingWorkingTimePercentage,
    hasNonReferenceContract: Object.keys(nonReferenceContracts).length > 0,
  }
}
